import { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Controller } from "react-hook-form";
import {
    Breadcrumb,
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    Row,
    Select,
    Space,
    Tag,
    Typography,
} from "antd";
import {
    ArrowLeftOutlined,
    CheckCircleOutlined,
    CheckOutlined,
    CloseOutlined,
    EnvironmentOutlined,
    KeyOutlined,
    MailOutlined,
    SafetyCertificateOutlined,
    TeamOutlined,
    UserOutlined,
} from "@ant-design/icons";

import { IPanelUser } from "../../../../interfaces";
import UpdatePasswordFormModal from "./UpdatePasswordFormModal";
import usePanelUserForm from "./usePanelUserForm";
import PageHeader from "../../../../components/PageHeader";
import { ThemeContext } from "../../../../contexts/ThemeContext";

const { Text } = Typography;

interface IProps {
    action: "Adicionar" | "Editar";
    user?: IPanelUser;
    onSubmit: (user: IPanelUser) => Promise<void>;
}

export default function PanelUserForm({ action, user, onSubmit }: IProps) {
    const {
        isSubmiting,
        isLoadingCities,
        isLoadingRolePermissions,
        isLoadingRoles,
        handleCloseUpdatePasswordModal,
        handleOpenUpdatePasswordModal,
        handleSubmit,
        handleSubmitForm,
        cities,
        roles,
        rolePermissions,
        control,
        errors,
        watchedCityIds,
        watchedRoleId,
        handleSelectAllCities,
        handleClearAllCities,
        isUpdatePasswordModalVisible,
    } = usePanelUserForm(onSubmit, user);

    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const navigate = useNavigate();

    const isEdit = action === "Editar";

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Breadcrumb Navigation Card */}
            <div
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}`,
                    padding: "10px 16px",
                    borderRadius: 8,
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/configuracoes/usuarios-painel">Usuários do Painel</Link>,
                        },
                        {
                            title: isEdit ? `Editar Usuário #${user?.id ?? "--"}` : "Novo Usuário",
                        },
                    ]}
                />
            </div>

            {/* Page Header */}
            <PageHeader
                icon={<TeamOutlined />}
                iconBg="linear-gradient(135deg, #1677ff 0%, #0958d9 100%)"
                title={isEdit ? `Editar Usuário: ${user?.name || ""}` : "Novo Usuário do Painel"}
                tag={
                    isEdit && user?.id ? (
                        <Tag color={user.active ? "success" : "error"}>
                            {user.active ? "Ativo" : "Inativo"} (ID #{user.id})
                        </Tag>
                    ) : undefined
                }
                subtitle={
                    isEdit
                        ? "Atualize as informações cadastrais, o perfil de acesso e os municípios autorizados deste usuário"
                        : "Cadastre um novo usuário administrativo e defina seu perfil e municípios de atuação"
                }
                actions={
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate("/configuracoes/usuarios-painel")}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 500,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Voltar para Usuários
                    </Button>
                }
            />

            {/* Form Card */}
            <Card
                style={{
                    borderRadius: 12,
                    border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                    background: isDark ? "#141414" : "#ffffff",
                    boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.03)",
                }}
                bodyStyle={{ padding: "20px 24px" }}
            >
                <Form
                    onFinish={handleSubmit(handleSubmitForm)}
                    autoComplete="off"
                    layout="vertical"
                >
                    {/* Section 1: Dados do Usuário */}
                    <div style={{ marginBottom: 20 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                            }}
                        >
                            <UserOutlined style={{ color: "#1677ff", fontSize: 18 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                    Identificação e Acesso
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Nome completo e e-mail institucional utilizado para autenticação no painel
                                </Text>
                            </div>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<strong>Nome Completo</strong>}
                                    required
                                    validateStatus={errors.name ? "error" : undefined}
                                    help={errors.name?.message}
                                >
                                    <Controller
                                        name="name"
                                        control={control}
                                        rules={{
                                            required: "Por favor, digite o nome completo!",
                                            minLength: {
                                                value: 2,
                                                message: "O nome deve ter pelo menos 2 caracteres.",
                                            },
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                placeholder="Ex: João da Silva"
                                                size="large"
                                                style={{ borderRadius: 8 }}
                                                prefix={<UserOutlined style={{ color: "#1677ff" }} />}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<strong>E-mail de Acesso</strong>}
                                    required
                                    validateStatus={errors.email ? "error" : undefined}
                                    help={errors.email?.message}
                                >
                                    <Controller
                                        name="email"
                                        control={control}
                                        rules={{
                                            required: "Por favor, digite o e-mail!",
                                            pattern: {
                                                value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                                message: "Por favor, informe um e-mail válido.",
                                            },
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                placeholder="Ex: joao.silva@municipio.gov.br"
                                                size="large"
                                                style={{ borderRadius: 8 }}
                                                prefix={<MailOutlined style={{ color: "#1677ff" }} />}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    {/* Section 2: Perfil e Permissões */}
                    <div style={{ marginBottom: 20 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                            }}
                        >
                            <SafetyCertificateOutlined style={{ color: "#722ed1", fontSize: 18 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                    Perfil e Permissões
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Defina o nível de acesso e visualize as funcionalidades disponíveis para este perfil
                                </Text>
                            </div>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<strong>Perfil de Acesso</strong>}
                                    required
                                    validateStatus={errors.role_id ? "error" : undefined}
                                    help={errors.role_id?.message}
                                >
                                    <Controller
                                        name="role_id"
                                        control={control}
                                        rules={{
                                            required: "Por favor, selecione o perfil do usuário!",
                                        }}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                options={roles.map((r) => ({
                                                    value: r.id,
                                                    label: r.name,
                                                }))}
                                                placeholder="Selecione o perfil de acesso..."
                                                disabled={isLoadingRoles}
                                                loading={isLoadingRoles}
                                                size="large"
                                                style={{ width: "100%", borderRadius: 8 }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <strong>Permissões Vinculadas ao Perfil</strong>
                                            {watchedRoleId && (
                                                <Tag color="purple" style={{ borderRadius: 10, margin: 0, fontSize: 11 }}>
                                                    {rolePermissions.length} regra{rolePermissions.length === 1 ? "" : "s"}
                                                </Tag>
                                            )}
                                        </div>
                                    }
                                >
                                    <Select
                                        mode="multiple"
                                        disabled
                                        loading={isLoadingRolePermissions}
                                        value={rolePermissions.map((p) => p.id)}
                                        options={rolePermissions.map((p) => ({
                                            value: p.id,
                                            label: p.description || p.name,
                                        }))}
                                        placeholder={
                                            watchedRoleId
                                                ? isLoadingRolePermissions
                                                    ? "Carregando permissões..."
                                                    : "Nenhuma permissão específica neste perfil"
                                                : "Selecione um perfil ao lado para visualizar suas permissões..."
                                        }
                                        size="large"
                                        style={{ width: "100%", borderRadius: 8 }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    {/* Section 3: Municípios Vinculados */}
                    <div style={{ marginBottom: 24 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 10,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <EnvironmentOutlined style={{ color: "#1677ff", fontSize: 18 }} />
                                <div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b" }}>
                                            Municípios Autorizados
                                        </Text>
                                        <Tag color="geekblue" style={{ borderRadius: 10, margin: 0, fontWeight: 600, fontSize: 11 }}>
                                            {watchedCityIds.length} de {cities.length} selecionado{watchedCityIds.length === 1 ? "" : "s"}
                                        </Tag>
                                    </div>
                                    <Text type="secondary" style={{ fontSize: 11.5 }}>
                                        Selecione os municípios cujos dados e sorteios este usuário poderá consultar e gerenciar
                                    </Text>
                                </div>
                            </div>

                            <Space size={8}>
                                <Button
                                    size="small"
                                    type="dashed"
                                    icon={<CheckCircleOutlined />}
                                    onClick={handleSelectAllCities}
                                    disabled={isLoadingCities || watchedCityIds.length === cities.length}
                                    style={{ borderRadius: 6, fontSize: 12 }}
                                >
                                    Selecionar Todos
                                </Button>
                                <Button
                                    size="small"
                                    icon={<CloseOutlined />}
                                    onClick={handleClearAllCities}
                                    disabled={isLoadingCities || watchedCityIds.length === 0}
                                    style={{ borderRadius: 6, fontSize: 12 }}
                                >
                                    Limpar Seleção
                                </Button>
                            </Space>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Form.Item
                                    label={<strong>Municípios com Acesso Permitido</strong>}
                                    required
                                    validateStatus={errors.city_ids ? "error" : undefined}
                                    help={errors.city_ids?.message}
                                >
                                    <Controller
                                        name="city_ids"
                                        control={control}
                                        rules={{
                                            required: "Por favor, selecione ao menos um município!",
                                        }}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                mode="multiple"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                                }
                                                options={cities.map((c) => ({
                                                    value: c.id,
                                                    label: `${c.city} - ${c.uf} ${c.is_in_test ? "(Em teste)" : ""}`,
                                                }))}
                                                placeholder="Clique para pesquisar e selecionar os municípios autorizados..."
                                                disabled={isLoadingCities}
                                                loading={isLoadingCities}
                                                size="large"
                                                style={{ width: "100%", borderRadius: 8 }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    {/* Section 4: Alteração de Senha (quando em modo edição) */}
                    {isEdit && user && (
                        <div
                            style={{
                                background: isDark ? "#1f1f1f" : "#f8fafc",
                                border: `1px solid ${isDark ? "#303030" : "#e2e8f0"}`,
                                borderRadius: 8,
                                padding: "14px 18px",
                                marginBottom: 20,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 12,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <KeyOutlined style={{ color: "#faad14", fontSize: 20 }} />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b" }}>
                                        Segurança e Senha de Acesso
                                    </div>
                                    <div style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b" }}>
                                        Deseja redefinir ou criar uma nova senha para este usuário?
                                    </div>
                                </div>
                            </div>
                            <Button
                                icon={<KeyOutlined />}
                                onClick={handleOpenUpdatePasswordModal}
                                style={{ borderRadius: 6, fontWeight: 500 }}
                            >
                                Alterar Senha
                            </Button>
                        </div>
                    )}

                    <Divider style={{ margin: "20px 0 16px 0" }} />

                    {/* Action Footer */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
                        <Button
                            onClick={() => navigate("/configuracoes/usuarios-painel")}
                            disabled={isSubmiting}
                            style={{
                                height: 38,
                                borderRadius: 8,
                                fontWeight: 500,
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<CheckOutlined />}
                            loading={isSubmiting}
                            disabled={isSubmiting || isLoadingRoles || isLoadingCities}
                            style={{
                                height: 38,
                                borderRadius: 8,
                                fontWeight: 600,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                            }}
                        >
                            {isEdit ? "Salvar Alterações" : "Cadastrar Usuário"}
                        </Button>
                    </div>
                </Form>
            </Card>

            {/* Modal de Alteração de Senha */}
            {user && isUpdatePasswordModalVisible && (
                <UpdatePasswordFormModal
                    userId={user.id!}
                    onClose={handleCloseUpdatePasswordModal}
                    onFormSubmited={handleCloseUpdatePasswordModal}
                />
            )}
        </div>
    );
}
